import { IImageFormat } from "./format-interface";

/**
 * Registry class for managing supported image file formats.
 * Provides lookup and filter generation for UI dialogs.
 */
export class FormatRegistry {
  private formats: Map<string, IImageFormat> = new Map();

  /**
   * Registers a new image format handler.
   * If an extension is already registered, the new format will overwrite it.
   */
  register(format: IImageFormat): void {
    format.extensions.forEach((ext) => {
      this.formats.set(ext.toLowerCase(), format);
    });
  }

  /**
   * Retrieves a format handler based on file extension.
   * @param ext The extension including the dot (e.g. ".psd").
   */
  getByExtension(ext: string): IImageFormat | null {
    return this.formats.get(ext.toLowerCase()) || null;
  }

  /**
   * Returns all registered unique format handlers, sorted by priority (highest first).
   */
  getAll(): IImageFormat[] {
    const unique = Array.from(new Set(this.formats.values()));
    return unique.sort((a, b) => (b.priority || 10) - (a.priority || 10));
  }

  /**
   * Identifies the best format handler for the given binary content.
   * @param buffer The input binary data.
   */
  identify(buffer: ArrayBuffer): IImageFormat | null {
    // Check all formats by magic numbers, prioritizing higher priority ones
    const all = this.getAll();
    for (const format of all) {
        if (format.canHandle(buffer)) {
            return format;
        }
    }
    return null;
  }

  getOpenFilter(): string {
    const parts: string[] = [];
    const all = this.getAll();

    // 1. All Supported Image Files
    const registeredExtensions = all
      .flatMap(f => f.extensions)
      .map(ext => `*${ext.startsWith('.') ? ext : '.' + ext}`)
      .join(';');
    parts.push(`Supported Images|${registeredExtensions};*.hcie`);
    
    // 2. Individual Formats
    all.forEach(f => {
      const pattern = f.extensions.map(ext => `*${ext}`).join(';');
      parts.push(`${f.name} (${pattern})|${pattern}`);
    });

    parts.push("HCIE Project (*.hcie)|*.hcie");
    parts.push("All Files (*.*)|*.*");

    return parts.join("|");
  }

  /**
   * Generates a filter string for "Save" dialogs.
   */
  getSaveFilter(): string {
    const all = this.getAll();
    const parts: string[] = [];

    all
      .filter((f) => f.canWrite)
      .forEach((f) => {
        const pattern = "*" + f.extensions.join(";*");
        parts.push(`${f.name}|${pattern}`);
      });

    return parts.join("|");
  }
}

/**
 * Global singleton instance of the registry.
 */
export const imageFormatRegistry = new FormatRegistry();

// Default registrations (Deferred to avoid circular imports during module load if possible, 
// but since formats only import the interface, this is safe here)
import { PngFormat } from "./formats/png-format";
import { JpegFormat } from "./formats/jpeg-format";
import { PsdFormat } from "./formats/psd-format";
import { BmpFormat } from "./formats/bmp-format";
import { TgaFormat } from "./formats/tga-format";
import { IcoFormat } from "./formats/ico-format";
import { GifFormat } from "./formats/gif-format";
import { WebpFormat } from "./formats/webp-format";
import { KraFormat } from "./formats/kra-format";
import { XcfFormat } from "./formats/xcf-format";
import { PdnFormat } from "./formats/pdn-format";
import { SvgFormat } from "./formats/svg-format";

imageFormatRegistry.register(new PngFormat());
imageFormatRegistry.register(new JpegFormat());
imageFormatRegistry.register(new PsdFormat());
imageFormatRegistry.register(new BmpFormat());
imageFormatRegistry.register(new TgaFormat());
imageFormatRegistry.register(new IcoFormat());
imageFormatRegistry.register(new GifFormat());
imageFormatRegistry.register(new WebpFormat());
imageFormatRegistry.register(new KraFormat());
imageFormatRegistry.register(new XcfFormat());
imageFormatRegistry.register(new PdnFormat());
imageFormatRegistry.register(new SvgFormat());
